/**
 * O estudo do link em slides: uma seção por tela, passando para o lado pelas
 * setas (botões e teclado), pela barra de baixo ou arrastando no celular.
 *
 * Tamanho: no 100% o slide inteiro cabe na tela. No computador, testa algumas
 * larguras de conteúdo (gráfico com altura limitada) e fica com a que deixa a
 * letra maior, para usar os lados da tela larga. O vendedor/cliente aumenta ou
 * diminui com − e + (o que passa da altura rola dentro do slide). No celular
 * vale a versão de celular na largura da tela, rolando para baixo.
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTEUDO_CELULAR, CONTEUDO_TELA, montarPaginas, secoesDisponiveis } from './estudoPaginas';
import { carregarFonte } from './estudoPdf';
import { ESTILO_INTERATIVO, ligarInteracoes } from './estudoInterativo';
import type { Estudo } from './estudo';
import type { UserInfo } from './proposal-utils';

export const LARGURA_CELULAR = 640;
export const FATORES = [0.7, 0.8, 0.9, 1, 1.1, 1.25, 1.4, 1.6] as const;
const LARGURAS = [760, 860, 960, 1060, 1160, 1260];
const CHAVE_FATOR = 'estudo-fator';

/**
 * Zoom que faz o slide caber: o menor entre o da largura e o da altura.
 * `alturaDe(w)` mede o conteúdo (zoom 1) com a largura w.
 */
export const melhorEncaixe = (largura: number, altura: number, alturaDe: (w: number) => number) => {
  let melhor = { zoom: 0, largura: CONTEUDO_TELA };
  for (const w of LARGURAS) {
    const h = alturaDe(w);
    const zoom = Math.min((largura - 150) / w, (altura - 12) / Math.max(1, h));
    if (zoom > melhor.zoom) melhor = { zoom, largura: w };
  }
  return { ...melhor, zoom: Math.max(0.3, melhor.zoom) };
};

const lerFator = (): number => {
  try {
    const f = Number(localStorage.getItem(CHAVE_FATOR));
    return (FATORES as readonly number[]).includes(f) ? f : 1;
  } catch {
    return 1;
  }
};

const ESTILO_SLIDES = `
.estudo-trilho{display:flex;height:100%;transition:transform .55s cubic-bezier(.7,0,.2,1)}
.estudo-slide{flex:0 0 100%;height:100%;overflow-y:auto;overflow-x:hidden;background:#fff;display:flex;scrollbar-width:thin}
.estudo-slide.escura{background:#0F1A24}
.estudo-slide>div{margin:auto 0;width:100%}
.estudo-slide.escura>div{height:100%}
.estudo-slide section{border-top:0 !important;min-height:0 !important}
.estudo-slide.escura section{min-height:100% !important}
.estudo-slide section>div>*{transition:opacity .5s ease .2s,transform .5s ease .2s}
.estudo-slide:not(.ativo) section>div>*{opacity:0;transform:translateY(14px)}
@media (prefers-reduced-motion:reduce){.estudo-trilho,.estudo-slide section>div>*{transition:none !important}}
`;

type Props = { estudo: Estudo; especialista: UserInfo; fim?: ReactNode };

/**
 * A página em HTML, montada uma vez. Sem o memo, cada troca de slide refazia o
 * innerHTML e levava junto o encaixe (zoom e largura gravados no elemento) e a
 * estratégia escolhida.
 */
const Pagina = memo(({ html }: { html: string }) => <div dangerouslySetInnerHTML={{ __html: html }} />);
Pagina.displayName = 'Pagina';

export const EstudoSlides = ({ estudo, especialista, fim }: Props) => {
  const palco = useRef<HTMLDivElement>(null);
  const raiz = useRef<HTMLElement>(null);
  const [atual, setAtual] = useState(0);
  const [celular, setCelular] = useState(() => typeof window !== 'undefined' && window.innerWidth < LARGURA_CELULAR);
  const [fator, setFator] = useState(lerFator);
  const [medida, setMedida] = useState(0);

  useEffect(() => {
    void carregarFonte().then(() => setMedida((m) => m + 1));
  }, []);

  const ocultas = useMemo(() => (Array.isArray(estudo.opcoes.secoesOcultas) ? estudo.opcoes.secoesOcultas : []), [estudo]);
  const titulos = useMemo(
    () => ['Início', ...secoesDisponiveis(estudo).filter((s) => !ocultas.includes(s.id)).map((s) => s.titulo)],
    [estudo, ocultas],
  );
  const secoes = useMemo(() => {
    try {
      return montarPaginas(estudo, especialista, window.location.origin, celular ? 'celular' : 'tela');
    } catch (err) {
      console.error('Estudo: falha ao montar as páginas', err);
      return null;
    }
  }, [estudo, especialista, celular]);
  const total = secoes?.length ?? 0;

  const ir = useCallback((i: number) => setAtual(() => Math.max(0, Math.min(total - 1, i))), [total]);
  /** Relativo ao slide da vez (várias setas seguidas somam, mesmo antes de a tela redesenhar). */
  const andar = useCallback((passo: number) => setAtual((a) => Math.max(0, Math.min(total - 1, a + passo))), [total]);

  const mudarFator = useCallback((passo: -1 | 0 | 1) => {
    setFator((f) => {
      const k = passo === 0 ? FATORES.indexOf(1) : FATORES.indexOf(f as (typeof FATORES)[number]) + passo;
      const novo = FATORES[Math.max(0, Math.min(FATORES.length - 1, k))];
      try {
        localStorage.setItem(CHAVE_FATOR, String(novo));
      } catch {
        // Sem armazenamento (aba anônima): vale só nesta visita.
      }
      return novo;
    });
  }, []);

  // Largura do palco: decide celular × tela e refaz o encaixe ao redimensionar.
  useLayoutEffect(() => {
    const el = palco.current;
    if (!el) return;
    const medir = () => {
      setCelular((el.clientWidth || window.innerWidth) < LARGURA_CELULAR);
      setMedida((m) => m + 1);
    };
    const obs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    obs?.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      obs?.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);

  // Encaixe de cada slide.
  useLayoutEffect(() => {
    const el = palco.current;
    if (!el) return;
    const W = el.clientWidth || window.innerWidth;
    const H = el.clientHeight || window.innerHeight;
    el.querySelectorAll<HTMLElement>('.estudo-slide section').forEach((sec) => {
      const dentro = sec.firstElementChild as HTMLElement | null;
      if (!dentro) return;
      if (celular) {
        sec.style.setProperty('--estudo-zoom', String(Math.min(1.4, Math.max(0.8, (W - 24) / CONTEUDO_CELULAR)) * fator));
        return;
      }
      sec.style.setProperty('--estudo-zoom', '1');
      sec.querySelectorAll<SVGSVGElement>('svg').forEach((svg) => {
        const h = svg.viewBox?.baseVal?.height;
        if (h) svg.style.maxHeight = `${h}px`;
      });
      const { zoom, largura } = melhorEncaixe(W, H, (w) => {
        dentro.style.width = `${w}px`;
        return dentro.offsetHeight;
      });
      dentro.style.width = `${largura}px`;
      sec.style.setProperty('--estudo-zoom', String(Math.min(zoom * fator, (W - 40) / largura)));
    });
  }, [secoes, celular, fator, medida]);

  // Destaques, dicas dos gráficos e troca de estratégia.
  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    return ligarInteracoes(el);
  }, [secoes]);

  // Só o slide da vez recebe foco e leitor de tela; volta ao topo dele ao entrar.
  useEffect(() => {
    palco.current?.querySelectorAll<HTMLElement>('.estudo-slide').forEach((s, i) => {
      if (i === atual) {
        s.removeAttribute('inert');
        s.scrollTop = 0;
      } else {
        s.setAttribute('inert', '');
      }
    });
  }, [atual, secoes]);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      const alvo = e.target instanceof Element ? e.target : null;
      if (e.altKey || e.ctrlKey || e.metaKey || alvo?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        andar(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        andar(-1);
      } else if (e.key === 'Home') {
        ir(0);
      } else if (e.key === 'End') {
        ir(total - 1);
      } else if (e.key === '+' || e.key === '=') {
        mudarFator(1);
      } else if (e.key === '-') {
        mudarFator(-1);
      } else if (e.key === '0') {
        mudarFator(0);
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [total, ir, andar, mudarFator]);

  const toque = useRef<{ x: number; y: number } | null>(null);

  if (secoes === null) {
    return (
      <p className="px-6 py-24 text-center text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
        Não foi possível montar este estudo.
      </p>
    );
  }

  const seta =
    'absolute top-1/2 z-[3] hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-white shadow-lg transition-opacity hover:opacity-100 disabled:pointer-events-none disabled:opacity-0 sm:grid';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <style>{ESTILO_SLIDES + ESTILO_INTERATIVO}</style>
      <div
        ref={palco}
        className="relative min-h-0 flex-1 overflow-hidden"
        onTouchStart={(e) => {
          toque.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }}
        onTouchEnd={(e) => {
          const t = toque.current;
          toque.current = null;
          if (!t) return;
          const dx = e.changedTouches[0].clientX - t.x;
          const dy = e.changedTouches[0].clientY - t.y;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) andar(dx < 0 ? 1 : -1);
        }}
      >
        <main ref={raiz} className="estudo-trilho" style={{ transform: `translateX(${-100 * atual}%)` }}>
          {secoes.map((html, i) => (
            <div
              key={i}
              className={`estudo-slide${i === 0 ? ' escura' : ''}${i === atual ? ' ativo' : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} de ${total}: ${titulos[i] ?? ''}`}
            >
              <div>
                <Pagina html={html} />
                {i === total - 1 && fim}
              </div>
            </div>
          ))}
        </main>
        <button type="button" aria-label="Slide anterior" className={`${seta} left-4 opacity-90`} style={{ background: 'rgba(31,44,64,0.86)' }} disabled={atual === 0} onClick={() => andar(-1)}>
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button type="button" aria-label="Próximo slide" className={`${seta} right-4 opacity-90`} style={{ background: 'rgba(31,44,64,0.86)' }} disabled={atual === total - 1} onClick={() => andar(1)}>
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      <nav
        aria-label="Slides"
        className="flex flex-none items-center gap-3 border-t px-4 pt-2.5 sm:px-8"
        style={{ borderColor: 'rgba(196,173,142,0.25)', paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="flex min-w-0 flex-1 gap-1.5">
          {titulos.slice(0, total).map((t, i) => (
            <button
              key={i}
              type="button"
              onClick={() => ir(i)}
              aria-current={i === atual ? 'step' : undefined}
              aria-label={`Ir para ${t}`}
              className="group min-w-0 flex-1 py-1 text-left"
            >
              <span className="block h-[3px] overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.18)' }}>
                <span className="block h-full transition-all duration-500" style={{ width: i <= atual ? '100%' : '0%', background: '#C4AD8E' }} />
              </span>
              <span
                className="mt-1.5 hidden truncate text-[11px] font-semibold group-hover:text-white lg:block"
                style={{ color: i === atual ? '#fff' : 'rgba(255,255,255,0.6)' }}
              >
                {t}
              </span>
            </button>
          ))}
        </div>
        <div className="hidden items-center gap-0.5 rounded-full border px-1 py-0.5 sm:flex" style={{ borderColor: 'rgba(255,255,255,0.2)' }} role="group" aria-label="Tamanho">
          <button type="button" aria-label="Diminuir" onClick={() => mudarFator(-1)} className="h-7 w-7 rounded-full text-base font-bold hover:bg-white/10">
            −
          </button>
          <button type="button" aria-label="Tamanho original" onClick={() => mudarFator(0)} className="min-w-11 text-xs font-bold tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {Math.round(fator * 100)}%
          </button>
          <button type="button" aria-label="Aumentar" onClick={() => mudarFator(1)} className="h-7 w-7 rounded-full text-base font-bold hover:bg-white/10">
            +
          </button>
        </div>
        <span className="text-xs font-bold tabular-nums tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {String(atual + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </nav>
    </div>
  );
};
